import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDraft, clearPrefilledPick, updateDescription } from '../repository/draftsRepository';
import { DraftDetailResponse } from '../objects/draftTypes';
import DraftBoard from './DraftBoard';
import PickForm from './PickForm';
import EditPickModal from './EditPickModal';
import PrefillPickModal from './PrefillPickModal';

const DraftDetail: React.FC = () => {
    const { draftId } = useParams<{ draftId: string }>();
    const [draftData, setDraftData] = useState<DraftDetailResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingPick, setEditingPick] = useState<number | null>(null);
    const [prefillingPick, setPrefillingPick] = useState<{ pickNumber: number; drafter: string } | null>(null);
    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState('');
    const [descriptionSaving, setDescriptionSaving] = useState(false);

    const fetchDraft = useCallback(async () => {
        if (!draftId) return;

        const result = await getDraft(draftId);

        if ('error' in result) {
            setError(result.error);
        } else {
            setDraftData(result);
        }
        setLoading(false);
    }, [draftId]);

    useEffect(() => {
        fetchDraft();
    }, [fetchDraft]);

    // Poll for updates when it's not your turn or waiting for invites
    useEffect(() => {
        if (!draftData || draftData.draft.status === 'completed') {
            return;
        }
        // Don't poll if it's your turn and draft is in progress
        if (draftData.is_my_turn && draftData.draft.status === 'in_progress') {
            return;
        }

        const interval = setInterval(() => {
            fetchDraft();
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(interval);
    }, [draftData, fetchDraft]);

    const handlePickMade = () => {
        fetchDraft();
    };

    const handleEditPick = (pickNumber: number) => {
        setEditingPick(pickNumber);
    };

    const handleEditComplete = () => {
        setEditingPick(null);
        fetchDraft();
    };

    const handlePrefillPick = (pickNumber: number, drafter: string) => {
        setPrefillingPick({ pickNumber, drafter });
    };

    const handlePrefillComplete = () => {
        setPrefillingPick(null);
        fetchDraft();
    };

    const handleStartEditDescription = () => {
        setDescriptionDraft(draftData?.draft.description || '');
        setEditingDescription(true);
    };

    const handleSaveDescription = async () => {
        if (!draftId) return;
        setDescriptionSaving(true);
        const result = await updateDescription(draftId, descriptionDraft.trim());
        if ('error' in result) {
            setError(result.error);
        } else {
            setEditingDescription(false);
            fetchDraft();
        }
        setDescriptionSaving(false);
    };

    const handleClearPrefilledPick = async (pickNumber: number) => {
        if (!draftId) return;

        const result = await clearPrefilledPick(draftId, pickNumber);
        if ('error' in result) {
            setError(result.error);
        } else {
            fetchDraft();
        }
    };

    if (loading) {
        return <div className="loading">Loading draft...</div>;
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-message">{error}</div>
                <Link to="/" className="btn-secondary">Back to Drafts</Link>
            </div>
        );
    }

    if (!draftData) {
        return <div className="error-message">Draft not found</div>;
    }

    const { draft, current_drafter, is_my_turn, is_owner, invitation_statuses } = draftData;

    return (
        <div className="draft-detail">
            <div className="draft-header">
                <Link to="/" className="back-link">Back to Drafts</Link>
                <h1>{draft.name}</h1>
                <div className="draft-meta">
                    <span className={`status-badge ${draft.status}`}>
                        {draft.status === 'in_progress' ? 'In Progress' :
                         draft.status === 'waiting_for_invites' ? 'Waiting for Invites' : 'Completed'}
                    </span>
                    <span>{draft.num_rounds} rounds</span>
                    <span>{draft.drafters.length} drafters</span>
                </div>
            </div>

            <div className="draft-order">
                <h3>Draft Order</h3>
                <div className="drafter-list">
                    {draft.drafter_order.map((drafter, index) => {
                        const inviteStatus = invitation_statuses?.[drafter];
                        return (
                            <span
                                key={drafter}
                                className={`drafter-tag ${drafter === current_drafter ? 'current' : ''} ${inviteStatus === 'pending' ? 'pending' : ''}`}
                            >
                                {index + 1}. {drafter}
                                {drafter === draft.owner && ' (owner)'}
                                {inviteStatus === 'pending' && ' (pending)'}
                                {inviteStatus === 'declined' && ' (declined)'}
                            </span>
                        );
                    })}
                </div>
            </div>

            {draft.status === 'waiting_for_invites' && (
                <div className="turn-indicator">
                    <div className="waiting">
                        Waiting for all participants to accept their invitations...
                    </div>
                </div>
            )}

            {draft.status === 'in_progress' && (
                <div className="turn-indicator">
                    {is_my_turn ? (
                        <div className="your-turn">
                            <strong>It's your turn!</strong>
                            <PickForm
                                draftUuid={draft.uuid}
                                onPickMade={handlePickMade}
                            />
                        </div>
                    ) : (
                        <div className="waiting">
                            Waiting for <strong>{current_drafter}</strong> to pick...
                        </div>
                    )}
                </div>
            )}

            {draft.status === 'completed' && (
                <div className="draft-completed">
                    Draft completed!
                </div>
            )}

            <DraftBoard
                draft={draft}
                currentDrafter={current_drafter}
                isOwner={is_owner}
                onEditPick={handleEditPick}
                onPrefillPick={handlePrefillPick}
                onClearPrefilledPick={handleClearPrefilledPick}
            />

            {(draft.description || is_owner) && (
                <div className="draft-description">
                    <h3>Description</h3>
                    {editingDescription ? (
                        <div className="description-edit">
                            <textarea
                                value={descriptionDraft}
                                onChange={(e) => setDescriptionDraft(e.target.value)}
                                rows={3}
                                disabled={descriptionSaving}
                            />
                            <div className="description-edit-actions">
                                <button
                                    className="btn-secondary btn-small"
                                    onClick={() => setEditingDescription(false)}
                                    disabled={descriptionSaving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn-primary btn-small"
                                    onClick={handleSaveDescription}
                                    disabled={descriptionSaving}
                                >
                                    {descriptionSaving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="description-display">
                            {draft.description ? (
                                <p>{draft.description}</p>
                            ) : (
                                <p className="text-muted">No description yet.</p>
                            )}
                            {is_owner && (
                                <button
                                    className="btn-secondary btn-small"
                                    onClick={handleStartEditDescription}
                                >
                                    {draft.description ? 'Edit' : 'Add description'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {editingPick !== null && (
                <EditPickModal
                    draftUuid={draft.uuid}
                    pickNumber={editingPick}
                    currentValue={
                        draft.picks.find(p => p.pick_number === editingPick)?.value || ''
                    }
                    onClose={() => setEditingPick(null)}
                    onSave={handleEditComplete}
                />
            )}

            {prefillingPick !== null && (
                <PrefillPickModal
                    draftUuid={draft.uuid}
                    pickNumber={prefillingPick.pickNumber}
                    drafter={prefillingPick.drafter}
                    onClose={() => setPrefillingPick(null)}
                    onSave={handlePrefillComplete}
                />
            )}
        </div>
    );
};

export default DraftDetail;
